interface ServiceWorkerContainerLike {
  register(scriptUrl: string, options: RegistrationOptions): Promise<ServiceWorkerRegistration>;
}

interface EventTargetLike {
  addEventListener(type: string, listener: () => void): void;
}

export interface ServiceWorkerRegistrationEnvironment {
  serviceWorker?: ServiceWorkerContainerLike;
  document?: EventTargetLike & { visibilityState: DocumentVisibilityState };
  production: boolean;
  developmentEnabled: boolean;
}

export type RegisterPwaServiceWorker = () => Promise<ServiceWorkerRegistration | null>;

async function updateRegistration(registration: ServiceWorkerRegistration): Promise<void> {
  try {
    await registration.update();
  } catch {
    console.warn('[pwa] Не удалось проверить обновление service worker');
  }
}

export function createPwaServiceWorkerRegistrar(
  environment: ServiceWorkerRegistrationEnvironment,
): RegisterPwaServiceWorker {
  let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

  return async (): Promise<ServiceWorkerRegistration | null> => {
    if (registrationPromise) {
      return registrationPromise;
    }

    const serviceWorker = environment.serviceWorker;

    if (!serviceWorker || (!environment.production && !environment.developmentEnabled)) {
      return null;
    }

    registrationPromise = (async () => {
      try {
        const scriptUrl = environment.production ? '/sw.js' : '/dev-sw.js?dev-sw';
        const options: RegistrationOptions = {
          scope: '/',
          updateViaCache: 'none',
          ...(environment.production ? {} : { type: 'module' as const }),
        };
        const registration = await serviceWorker.register(scriptUrl, options);
        const requestUpdate = (): void => {
          void updateRegistration(registration);
        };

        environment.document?.addEventListener('visibilitychange', () => {
          if (environment.document?.visibilityState === 'visible') {
            requestUpdate();
          }
        });
        await updateRegistration(registration);

        return registration;
      } catch {
        console.error('[pwa] Не удалось зарегистрировать service worker');

        return null;
      }
    })();

    return registrationPromise;
  };
}

const browserEnvironment: ServiceWorkerRegistrationEnvironment = {
  serviceWorker: typeof navigator === 'undefined' ? undefined : navigator.serviceWorker,
  document: typeof document === 'undefined' ? undefined : document,
  production: import.meta.env.PROD,
  developmentEnabled: import.meta.env.VITE_PWA_DEV_ENABLED === 'true',
};

export const registerPwaServiceWorker = createPwaServiceWorkerRegistrar(browserEnvironment);
